import { Button, TreeNodeProps, TreeSelect } from 'antd';
import {
  ALL_DISTRICTS,
  findArea,
  findAreaByDistrict,
  INFLATED_DISTRICT,
  AreaData,
  DistrictMapProps,
} from 'constants/areaNDistricts';
import { useTranslation } from 'react-i18next';
import { Dispatch, SetStateAction, useEffect, useState, useMemo } from 'react';
import { TreeNode } from 'antd/lib/tree-select';
import { MenuActionsContainer, SecondaryTextButton, StyledDivider } from '../styledComponents';
import generateKey from '../../../../utils/generateKey';
import { useKeycloakReady } from 'hooks/useKeycloakReady';
import { useGetAreadistrictData } from 'all-in-one-shared-api';

type AreaTreeSelectProps = {
  districtValue: string[] | undefined;
  handleOnDistrictChange: Dispatch<SetStateAction<string[] | undefined>>;
  handleOnAreaChange: (val: string) => void;
};

export const AreaTreeSelect = ({ districtValue, handleOnDistrictChange, handleOnAreaChange }: AreaTreeSelectProps) => {
  const { t, i18n } = useTranslation();
  const { token } = useKeycloakReady();
  const [openDistrict, setOpenDistrict] = useState(false);
  // const inflatedDistricts = districtValue?.map((dVal) => INFLATED_DISTRICT(dVal)) ?? [];
  const [treeKey, setTreeKey] = useState<string>();
  const reset = () => {
    handleOnAreaChange('');
    handleOnDistrictChange([]);
  };

  const { data: responseList, shouldFetch } = useGetAreadistrictData('getNotiSettings', token ?? '');

  const listData: { areaData: AreaData[]; districtData: DistrictMapProps } = useMemo(() => {
    if (!responseList) return { areaData: [], districtData: {} };
    responseList.areaData[0].name = 'ALL';
    const districts = formatDistrictData(responseList.districtData);
    return { areaData: responseList.areaData, districtData: districts };
  }, [responseList]);

  const inflatedDistricts = districtValue?.map((dVal) => INFLATED_DISTRICT(dVal, listData)) ?? [];
  useEffect(() => {
    shouldFetch(!!token);
  }, [shouldFetch, token]);

  const onChange = (values: string[]) => {
    const trimmed = values.map((str) => str.split('|')[0]);
    const { area, includesArea, districts } = findArea(trimmed, listData.districtData);
    handleOnAreaChange(area);

    if (values.includes('All Districts|全選地區')) {
      handleOnAreaChange('All Districts|全選地區');
      return handleOnDistrictChange([ALL_DISTRICTS]);
    }

    if (includesArea) {
      handleOnDistrictChange([area, ...districts]);
    } else {
      handleOnDistrictChange(trimmed);
    }
  };

  const onDeselect = (val: string) => {
    const trimmed = val.split('|')[0];
    const targetDistricts = listData.districtData[trimmed]?.map((a) => a.name);
    if (targetDistricts?.length) {
      reset();
    } else {
      const area = findAreaByDistrict(trimmed, listData.districtData);
      handleOnDistrictChange((prev: string[] | undefined) => {
        return prev?.filter((a) => a !== trimmed && a !== area);
      });
    }
  };

  const onResetClicked = () => {
    reset();
  };

  function formatDistrictData(data: DistrictMapProps) {
    const newData: DistrictMapProps = {};

    Object.keys(data).forEach((area) => {
      if (area === 'All') {
        return (newData['ALL'] = [{ name: 'All Districts', name_zh: '全選地區', area: 'ALL' }]);
      }
      newData[area] = data[area].map((item) => {
        return {
          name: item.name,
          name_zh: item.name_zh,
          area: area,
        };
      });
    });

    return newData;
  }

  useEffect(() => {
    setTreeKey(generateKey());
  }, [i18n.language]);

  return (
    <TreeSelect
      key={treeKey}
      style={{ width: '100%' }}
      placeholder={t('panelList.selectArea')}
      value={inflatedDistricts}
      onChange={onChange}
      onDeselect={onDeselect}
      multiple
      treeCheckable
      showArrow
      open={openDistrict}
      onDropdownVisibleChange={(visible) => setOpenDistrict(visible)}
      maxTagCount={1}
      tagRender={(node) => {
        return (
          <span className="ant-select-selection-item">
            <span className="ant-select-selection-item-content">{node.label}</span>
          </span>
        );
      }}
      showSearch={true}
      filterTreeNode={(search, item: TreeNodeProps) => {
        return item?.value?.toLowerCase().indexOf(search.toLowerCase()) >= 0;
      }}
      dropdownRender={(menu) => (
        <>
          {menu}
          <StyledDivider />
          <MenuActionsContainer>
            <SecondaryTextButton type="text" onClick={onResetClicked}>
              {t('common.reset')}
            </SecondaryTextButton>
            <Button type="primary" onClick={() => setOpenDistrict(false)}>
              {t('common.ok')}
            </Button>
          </MenuActionsContainer>
        </>
      )}
    >
      {listData.areaData.map((area) => (
        <TreeNode
          checkable={false}
          selectable={false}
          key={`${area.name}_area`}
          value={`${area.name}_area`}
          label={i18n.language === 'en' ? area.name : area.name_zh}
        >
          {listData.districtData[area.name]?.length > 1 ? (
            <TreeNode
              key={`${area.name}|${area.name_zh}`}
              value={`${area.name}|${area.name_zh}`}
              label={t('common.all')}
            />
          ) : null}
          {listData.districtData[area.name]?.map((district) => (
            <TreeNode
              key={`${district.name}|${district.name_zh}`}
              value={`${district.name}|${district.name_zh}`}
              label={i18n.language === 'en' ? district.name : district.name_zh}
            />
          ))}
        </TreeNode>
      ))}
    </TreeSelect>
  );
};
